import React, { useEffect, useState, Component } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiRequest } from '../utils/api'
import { ArrowLeft, Edit2, Trash2, X, Save, UserMinus, Crown, Zap, CreditCard, Users, Calendar, Receipt } from 'lucide-react'
import PageHeader from '../components/admin/PageHeader'
import StatCard from '../components/admin/StatCard'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog'

class TeamDetailErrorBoundary extends Component {
  state = { hasError: false, error: null }
  static getDerivedStateFromError(error) { return { hasError: true, error } }
  render() {
    if (this.state.hasError) {
      return (
        <div className="text-center py-12">
          <p className="text-rose-600 font-medium mb-2">Page crashed</p>
          <p className="text-sm text-gray-500 mb-4">{this.state.error?.message}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>Reload</Button>
        </div>
      )
    }
    return this.props.children
  }
}

export default function TeamDetail() {
  return <TeamDetailErrorBoundary><TeamDetailInner /></TeamDetailErrorBoundary>
}

function TeamDetailInner() {
  const { teamId } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [saveLoading, setSaveLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState(null)

  useEffect(() => { loadTeam() }, [teamId])

  const loadTeam = async () => {
    try {
      const teamData = await apiRequest(`/api/admin/teams/${teamId}`)
      setData(teamData)
      setEditForm({
        name: teamData.team.name || '', sport: teamData.team.sport || '',
        age_group: teamData.team.age_group || '', season: teamData.team.season || '',
        description: teamData.team.description || '', location: teamData.team.location || ''
      })
    } catch (err) { console.error('Error loading team:', err) }
    finally { setLoading(false) }
  }

  const handleSaveEdit = async () => {
    setSaveLoading(true)
    try { await apiRequest(`/api/admin/teams/${teamId}`, { method: 'PUT', body: JSON.stringify(editForm) }); setEditMode(false); loadTeam() }
    catch (err) { alert(err.message) } finally { setSaveLoading(false) }
  }

  const handleDelete = () => {
    setConfirmDialog({
      message: 'Are you sure you want to delete this team? This action cannot be undone.',
      action: async () => {
        setActionLoading(true)
        try { await apiRequest(`/api/admin/teams/${teamId}`, { method: 'DELETE' }); window.location.href = '/teams' }
        catch (err) { alert(err.message) } finally { setActionLoading(false) }
      }
    })
  }

  const handleRemoveMember = async (userId, userName) => {
    if (!confirm(`Remove ${userName} from this team?`)) return
    try { await apiRequest(`/api/admin/teams/${teamId}/members/${userId}/remove`, { method: 'POST' }); loadTeam() }
    catch (err) { alert(err.message) }
  }

  const handleChangeRole = async (userId, newRole) => {
    try { await apiRequest(`/api/admin/teams/${teamId}/members/${userId}/role?role=${newRole}`, { method: 'POST' }); loadTeam() }
    catch (err) { alert(err.message) }
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-2 border-indigo-600 border-t-transparent"></div></div>
  if (!data) return <div className="text-center py-12 text-gray-500">Team not found</div>

  const { team, members, events, invoices, stats } = data
  const sportEmoji = { soccer: '⚽', basketball: '🏀', baseball: '⚾', football: '🏈', hockey: '🏒', volleyball: '🏐', tennis: '🎾', swimming: '🏊' }

  return (
    <div className="space-y-6">
      <Link to="/teams" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4" /> Back to Teams
      </Link>

      {/* Team Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-6">
            <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center flex-shrink-0">
              <span className="text-3xl">{sportEmoji[team.sport] || '🏆'}</span>
            </div>
            <div className="flex-1">
              {editMode ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>Team Name</Label><Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} /></div>
                    <div>
                      <Label>Sport</Label>
                      <Select value={editForm.sport} onValueChange={(val) => setEditForm({...editForm, sport: val})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['soccer','basketball','baseball','football','hockey','volleyball','tennis','swimming','other'].map(s => (
                            <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Age Group</Label><Input value={editForm.age_group} onChange={(e) => setEditForm({...editForm, age_group: e.target.value})} placeholder="e.g., U12, U14" /></div>
                    <div><Label>Season</Label><Input value={editForm.season} onChange={(e) => setEditForm({...editForm, season: e.target.value})} placeholder="e.g., Spring 2025" /></div>
                    <div><Label>Location</Label><Input value={editForm.location} onChange={(e) => setEditForm({...editForm, location: e.target.value})} placeholder="City, State" /></div>
                  </div>
                  <div><Label>Description</Label><textarea value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none" rows={3} /></div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveEdit} disabled={saveLoading}><Save className="w-4 h-4 mr-2" />{saveLoading ? 'Saving...' : 'Save Changes'}</Button>
                    <Button variant="outline" onClick={() => setEditMode(false)}><X className="w-4 h-4 mr-2" />Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-900">{team.name}</h1>
                    <Badge className="bg-gray-100 text-gray-700 capitalize">{team.sport}</Badge>
                  </div>
                  {team.age_group && <p className="text-gray-500">{team.age_group}</p>}
                  {team.season && <p className="text-sm text-gray-500">{team.season}</p>}
                  {team.location && <p className="text-sm text-gray-500">{team.location}</p>}
                  {team.description && <p className="text-gray-600 mt-2">{team.description}</p>}
                  <p className="text-sm text-gray-400 mt-2">Created {team.created_at ? new Date(team.created_at).toLocaleDateString() : 'Unknown'}</p>
                </>
              )}
            </div>
            {!editMode && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setEditMode(true)}><Edit2 className="w-4 h-4 mr-2" />Edit</Button>
                <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}><Trash2 className="w-4 h-4 mr-2" />Delete</Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Members" value={stats?.member_count || 0} icon={Users} color="indigo" />
        <StatCard title="Events" value={stats?.event_count || 0} icon={Calendar} color="emerald" />
        <StatCard title="Total Invoiced" value={`$${((stats?.total_invoiced || 0) / 100).toFixed(0)}`} icon={Receipt} color="blue" />
        <StatCard title="Invoices" value={invoices?.length || 0} icon={CreditCard} color="amber" />
      </div>

      {/* Plan Management */}
      <PlanManager team={team} teamId={teamId} onUpdate={() => window.location.reload()} />

      {/* Members */}
      <Card>
        <CardHeader><CardTitle>Members ({members?.length || 0})</CardTitle></CardHeader>
        <CardContent>
          {members?.length > 0 ? (
            <div className="space-y-2">
              {members.map(({ member, user }) => (
                <div key={member.user_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      {user?.picture ? <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" /> : <span className="text-gray-600 font-medium">{user?.name?.charAt(0) || '?'}</span>}
                    </div>
                    <div>
                      <Link to={`/users/${member.user_id}`} className="font-medium hover:underline text-gray-900">{user?.name || 'Unknown User'}</Link>
                      <div className="flex items-center gap-2">
                        <Badge className={member.role === 'owner' ? 'bg-amber-100 text-amber-700' : member.role === 'admin' || member.role === 'coach' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}>{member.role || 'member'}</Badge>
                        {user?.email && <span className="text-xs text-gray-500">{user.email}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={member.role || 'parent'} onChange={(e) => handleChangeRole(member.user_id, e.target.value)} className="text-sm px-2 py-1 border border-gray-200 rounded-lg outline-none bg-white text-gray-900">
                      <option value="owner">Owner</option><option value="admin">Admin</option><option value="coach">Coach</option><option value="parent">Parent</option><option value="player">Player</option>
                    </select>
                    <Button variant="ghost" size="icon" className="text-rose-500 hover:bg-rose-50" onClick={() => handleRemoveMember(member.user_id, user?.name || 'this user')} title="Remove from team">
                      <UserMinus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500">No members</p>}
        </CardContent>
      </Card>

      {/* Events */}
      <Card>
        <CardHeader><CardTitle>Recent Events ({events?.length || 0})</CardTitle></CardHeader>
        <CardContent>
          {events?.length > 0 ? (
            <div className="space-y-2">
              {events.slice(0, 10).map((event) => (
                <div key={event.event_id} className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-900">{event.title}</p>
                    <p className="text-sm text-gray-500 capitalize">{event.event_type || event.type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-600">{event.event_date || event.start_time ? new Date(event.event_date || event.start_time).toLocaleDateString() : '-'}</p>
                    <p className="text-xs text-gray-500">{event.location}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500">No events</p>}
        </CardContent>
      </Card>

      {/* Payments */}
      <Card>
        <CardHeader><CardTitle>Payments ({invoices?.length || 0})</CardTitle></CardHeader>
        <CardContent>
          {invoices?.length > 0 ? (
            <div className="space-y-2">
              {invoices.map((invoice) => (
                <div key={invoice.invoice_id} className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-900">{invoice.description || 'Payment'}</p>
                    <p className="text-sm text-gray-500">{invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : '-'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-gray-900">${((invoice.amount || 0) / 100).toFixed(2)}</p>
                    <Badge className={invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : invoice.status === 'refunded' ? 'bg-gray-100 text-gray-700' : 'bg-amber-100 text-amber-700'}>{invoice.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500">No payments</p>}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) setConfirmDialog(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Action</DialogTitle>
            <DialogDescription>{confirmDialog?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancel</Button>
            <Button onClick={() => { const a = confirmDialog?.action; setConfirmDialog(null); a?.() }}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function PlanManager({ team, teamId, onUpdate }) {
  const [selectedPlan, setSelectedPlan] = useState(team?.plan || 'free')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [showChange, setShowChange] = useState(false)
  const [msg, setMsg] = useState('')

  const currentPlan = team?.plan || 'free'
  const isPaid = ['team_pro_monthly','team_pro_annual','club_monthly','club_annual','club_pro_monthly','club_pro_annual','premium_monthly','premium_annual','premium'].includes(currentPlan)
  const isTrial = currentPlan === 'trial'
  const trialEnd = team?.trial_end_date ? new Date(team.trial_end_date) : null
  const trialDaysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - Date.now()) / (1000*60*60*24))) : null

  const handleSave = async () => {
    setSaving(true); setMsg('')
    try {
      await apiRequest(`/api/admin/teams/${teamId}/plan`, { method: 'PUT', body: JSON.stringify({ plan: selectedPlan, reason: reason || 'Admin manual upgrade' }) })
      setMsg('Plan updated!'); setShowChange(false); setTimeout(() => onUpdate(), 1000)
    } catch (err) { setMsg(err.message) } finally { setSaving(false) }
  }

  const plans = [['free', 'Free'], ['trial', 'Trial (7 day)'], ['team_pro_monthly', 'Team Pro Monthly'], ['team_pro_annual', 'Team Pro Annual'], ['club_monthly', 'Club Monthly'], ['club_annual', 'Club Annual'], ['club_pro_monthly', 'Club Pro Monthly'], ['club_pro_annual', 'Club Pro Annual']]

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-gray-500" />Subscription</CardTitle>
          {!showChange && <Button variant="outline" size="sm" onClick={() => setShowChange(true)}><Zap className="w-3.5 h-3.5 mr-1" />Change Plan</Button>}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="p-3 rounded-xl bg-gray-50 min-w-[120px]">
            <p className="text-xs text-gray-500">Current Plan</p>
            <p className="text-sm font-semibold text-gray-900 capitalize mt-0.5">{currentPlan.replace(/_/g, ' ')}</p>
          </div>
          <div className="p-3 rounded-xl bg-gray-50 min-w-[120px]">
            <p className="text-xs text-gray-500">Status</p>
            <p className={`text-sm font-semibold mt-0.5 ${isPaid ? 'text-emerald-600' : isTrial ? 'text-amber-600' : 'text-gray-500'}`}>
              {isPaid ? 'Active' : isTrial ? `Trial (${trialDaysLeft}d left)` : 'Free'}
            </p>
          </div>
          {isTrial && trialEnd && (
            <div className="p-3 rounded-xl bg-gray-50 min-w-[120px]">
              <p className="text-xs text-gray-500">Trial Ends</p>
              <p className="text-sm font-semibold text-gray-900 mt-0.5">{trialEnd.toLocaleDateString()}</p>
            </div>
          )}
          {team?.stripe_subscription_id && (
            <div className="p-3 rounded-xl bg-gray-50 min-w-[120px]">
              <p className="text-xs text-gray-500">Stripe</p>
              <p className="text-sm font-semibold text-gray-900 mt-0.5 truncate">{team.stripe_subscription_id.slice(0,16)}...</p>
            </div>
          )}
        </div>
        {msg && <div className={`mb-3 p-2 rounded-lg text-sm ${msg.includes('updated') ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{msg}</div>}
        {showChange && (
          <div className="border-t border-gray-200 pt-4 space-y-3">
            <p className="text-sm text-gray-600 font-medium">Set plan (no payment required):</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {plans.map(([id, label]) => (
                <button key={id} onClick={() => setSelectedPlan(id)} className={`p-2.5 rounded-xl border text-sm text-left transition ${selectedPlan === id ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-medium' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>{label}</button>
              ))}
            </div>
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g., Partner team, comp upgrade, customer support" />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving || selectedPlan === currentPlan}>{saving ? 'Saving...' : `Set to ${selectedPlan.replace(/_/g, ' ')}`}</Button>
              <Button variant="ghost" onClick={() => { setShowChange(false); setSelectedPlan(currentPlan) }}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
